import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { getRecipes } from '../api';
import './RecipeSelector.css';

const RecipeSelector = ({ onSelect, onClose, selectedDate, mealType, theme = 'dark' }) => {
  const [recipes, setRecipes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedRecipe, setExpandedRecipe] = useState(null);
  const [favoriteRecipes, setFavoriteRecipes] = useState(() => {
    if (typeof window === 'undefined') return new Set();
    try {
      const stored = window.localStorage.getItem('favoriteRecipes');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch (error) {
      console.warn('No se pudieron cargar favoritos desde localStorage', error);
      return new Set();
    }
  });

  useEffect(() => {
    loadRecipes();
  }, [searchTerm, mealType]);

  const loadRecipes = async () => {
    setLoading(true);
    try {
      const response = await getRecipes(searchTerm, mealType);
      const sortedRecipes = sortRecipes(response.data);
      setRecipes(sortedRecipes);
    } catch (error) {
      console.error('Error cargando recetas:', error);
    } finally {
      setLoading(false);
    }
  };

  const sortRecipes = (recipeList) => {
    const target = mealType ? mealType.toLowerCase() : '';

    const getGroup = (recipe) => {
      const type = (recipe.meal_type || '').toLowerCase();
      const isFavorite = favoriteRecipes.has(recipe.id);
      const sameType = type === target;

      if (isFavorite && sameType) return 0;
      if (isFavorite && !sameType) return 1;
      if (!isFavorite && sameType) return 2;
      return 3;
    };

    return [...recipeList].sort((a, b) => {
      const groupDiff = getGroup(a) - getGroup(b);
      if (groupDiff !== 0) return groupDiff;
      return a.title.localeCompare(b.title);
    });
  };

  const getMealIcon = (type) => {
    const icons = {
      comida: '🍽️',
      cena: '🌙',
      desayuno: '☕',
      almuerzo: '🥗',
    };
    return icons[type] || '🍴';
  };

  const parseIngredients = (ingredients) => {
    if (!ingredients) return [];
    return ingredients.split(';').map(ing => ing.trim()).filter(ing => ing.length > 0);
  };

  const toggleExpand = (recipeId) => {
    setExpandedRecipe(prev => (prev === recipeId ? null : recipeId));
  };

  return (
    <div className={`recipe-selector-overlay ${theme}-theme`} onClick={onClose}>
      <div className="recipe-selector" onClick={(e) => e.stopPropagation()}>
        <div className="recipe-selector-header">
          <h3>
            Agregar a {mealType.charAt(0).toUpperCase() + mealType.slice(1)} - {format(selectedDate, 'd MMMM', { locale: es })}
          </h3>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>
        
        <div className="recipe-selector-search">
          <input
            type="text"
            placeholder="Buscar receta..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button className="btn-primary">Nueva</button>
        </div>

        <div className="recipe-list">
          {loading ? (
            <div className="loading">Cargando...</div>
          ) : recipes.length === 0 ? (
            <div className="no-recipes">No se encontraron recetas</div>
          ) : (
            recipes.map((recipe) => {
              const isExpanded = expandedRecipe === recipe.id;
              const ingredientList = parseIngredients(recipe.ingredients);

              return (
                <div key={recipe.id} className={`recipe-item ${isExpanded ? 'expanded' : ''}`}>
                  <div
                    className="recipe-main"
                    onClick={() => toggleExpand(recipe.id)}
                  >
                    <div className="recipe-icon">{getMealIcon(recipe.meal_type)}</div>
                    <div className="recipe-info">
                      <div className="recipe-name">{recipe.title}</div>
                      <div className="recipe-details">
                        <span>{recipe.meal_type ? recipe.meal_type.charAt(0).toUpperCase() + recipe.meal_type.slice(1) : ''}</span>
                      </div>
                      <div className="recipe-status">
                        <span className="status-badge calories">
                          {recipe.calories ? `${recipe.calories} kcal` : 'Sin calorías'}
                        </span>
                      </div>
                    </div>
                    <div className="recipe-actions">
                      <button
                        className="btn-add-recipe"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelect(recipe.id);
                        }}
                      >
                        +
                      </button>
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="recipe-ingredients">
                      <strong>Ingredientes:</strong>
                      <ul>
                        {ingredientList.map((ingredient, idx) => (
                          <li key={`${recipe.id}-ingredient-${idx}`}>{ingredient}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default RecipeSelector;
